'use client';
import { ABTestConfig } from '@data/surveyConfigs';
import { setExperimentCookie } from '@/components/utils/features';
import { sendGTMEvent } from '@next/third-parties/google';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { setNewRelicCustomAttribute } from '@/components/shared/NewRelicClient';

type ExperimentVariants = Record<string, string>;

const ABTestContext = createContext<ExperimentVariants>({});

export const useABTestVariant = (experimentId: string) => {
  const variants = useContext(ABTestContext);
  return variants[experimentId] || null;
};

function validateExperimentConfig(config: ABTestConfig): void {
  if (config.defaultVariantIndex < 0 || config.defaultVariantIndex >= config.variants.length) {
    throw new Error('Invalid experiment configuration: defaultIndex is out of bounds');
  }

  const totalPercentage = config.variants.reduce((sum, variant) => sum + variant.percentage, 0);

  if (totalPercentage !== 100) {
    throw new Error(
      `Invalid experiment configuration: variant percentages must sum to 100. Current sum: ${totalPercentage}`,
    );
  }
}

const isExperimentEnabled = (
  { utmCampaignToEnable, utmCampaignToDisable }: ABTestConfig,
  configs: ABTestConfig[],
): boolean => {
  const utmCampaign = new URLSearchParams(window.location.search).get('utm_campaign');

  // Check if experiment should be disabled
  if (utmCampaignToDisable && utmCampaign && utmCampaign.includes(utmCampaignToDisable)) {
    return false;
  }

  if (utmCampaignToEnable) {
    return !!utmCampaign && utmCampaign.includes(utmCampaignToEnable);
  }
  return (
    !utmCampaign ||
    !configs.some(
      ({ utmCampaignToEnable }) =>
        !!utmCampaignToEnable && utmCampaign.includes(utmCampaignToEnable),
    )
  );
};

const useVariantSelection = (configs: ABTestConfig[]) => {
  configs.forEach((config) => {
    validateExperimentConfig(config);
  });
  const [variants, setVariants] = useState<ExperimentVariants>({});

  useEffect(() => {
    const newVariants: ExperimentVariants = {};

    configs.forEach((config) => {
      const { variants, cookieName, variantParam, gtmEventName, experiment } = config;
      const defaultVariant = config.variants[config.defaultVariantIndex].id;

      const setVariantAndSendGTMEvent = (variant: string) => {
        newVariants[experiment] = variant;
        setExperimentCookie(variant);
        if (gtmEventName) {
          sendGTMEvent({ event: gtmEventName, selectedVariant: variant, experimentId: experiment });
        }

        // Send experiment data to New Relic
        // Using persist=true to maintain the attributes across page loads within the same session
        setNewRelicCustomAttribute('experiment', experiment, true);
        setNewRelicCustomAttribute('experimentVariant', variant, true);
      };

      const setRandomVariant = () => {
        const randomValue = Math.random() * 100;
        let cumulativePercentage = 0;

        for (const variant of variants) {
          cumulativePercentage += variant.percentage;
          if (randomValue < cumulativePercentage) {
            setVariantAndSendGTMEvent(variant.id);
            return;
          }
        }

        if (defaultVariant) setVariantAndSendGTMEvent(defaultVariant);
      };

      const getVariantFromParams = () => {
        if (!variantParam) {
          return;
        }
        const params = new URLSearchParams(window.location.search);
        const variantFromParams = params.get(variantParam);
        if (variantFromParams && isValidVariant(variantFromParams)) {
          return variantFromParams;
        }
      };

      const getVariantFromCookie = () => {
        const cookieValue = Cookies.get(cookieName);
        const variantFromCookie = cookieValue?.split('&').find((v) => isValidVariant(v));
        if (variantFromCookie) {
          return variantFromCookie;
        }
      };

      const isValidVariant = (value: string): boolean => {
        return variants.some((v) => v.id === value);
      };

      if (isExperimentEnabled(config, configs)) {
        const variantFromParams = getVariantFromParams();
        if (variantFromParams) {
          setVariantAndSendGTMEvent(variantFromParams);
          return;
        }

        const variantFromCookie = getVariantFromCookie();
        if (variantFromCookie) {
          setVariantAndSendGTMEvent(variantFromCookie);
          return;
        }

        setRandomVariant();
      } else {
        newVariants[experiment] = defaultVariant;
      }
    });

    setVariants(newVariants);
  }, [configs]);

  return variants;
};

export const ABTestProvider = ({
  children,
  configs,
}: {
  configs: ABTestConfig[];
  children: ReactNode;
}) => {
  const variants = useVariantSelection(configs);

  return <ABTestContext.Provider value={variants}>{children}</ABTestContext.Provider>;
};
